import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Sparkles, ShieldCheck, Star, Clock, ArrowRight } from 'lucide-react';
import { PremiumButton, GoldDivider, FlowerMotif } from './premium/Decor';
import HeroFlipbook from './HeroFlipbook';
import MaroonAmbientBackdrop from './premium/MaroonAmbientBackdrop';

/**
 * Premium Hero — deep-maroon variant with cosmic ambient backdrop:
 * - Dark maroon gradient background with animated drift orbs + twinkling stars
 * - Two-column layout (flipbook left, content right) on lg+
 * - Gold accents, gold shimmer headline, animated entrances
 * - Top urgency strip kept (countdown + live-customer counter)
 */
const HeroSection = ({ onOpenForm }) => {
  const { language } = useLanguage();
  const isHi = language === 'hi';
  const [timeLeft, setTimeLeft] = useState({ hours: 2, minutes: 47, seconds: 33 });
  const [customerCount, setCustomerCount] = useState(10847);

  useEffect(() => {
    const t = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return { ...prev, seconds: prev.seconds - 1 };
        if (prev.minutes > 0) return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        if (prev.hours > 0) return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
        return { hours: 23, minutes: 59, seconds: 59 };
      });
    }, 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const i = setInterval(() => setCustomerCount(c => c + Math.floor(Math.random() * 2)), 30000);
    return () => clearInterval(i);
  }, []);

  const fmt = (n) => String(n).padStart(2, '0');

  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden text-brand-gold-light"
      style={{
        background:
          'linear-gradient(180deg, #4A0912 0%, #73111E 30%, #5C0E18 70%, #3a050d 100%)',
      }}
    >
      {/* Animated cosmic backdrop */}
      <MaroonAmbientBackdrop variant="hero" />

      {/* Top urgency bar — gold gradient strip */}
      <div className="relative z-10 bg-gradient-to-r from-brand-gold-dark via-brand-gold to-brand-gold-dark text-brand-maroon-dark border-b border-brand-gold-light/40">
        <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-center gap-3 text-sm flex-wrap">
          <Clock className="w-4 h-4 animate-pulse-soft" />
          <span className={`font-semibold ${isHi ? 'font-hindi' : 'font-outfit'}`}>
            {isHi ? 'विशेष ऑफर समाप्त होगा' : 'Special offer ends in'}
          </span>
          <span className="font-mono font-black tracking-wider bg-brand-maroon-dark text-brand-gold-light rounded-md px-2 py-0.5">
            {fmt(timeLeft.hours)}:{fmt(timeLeft.minutes)}:{fmt(timeLeft.seconds)}
          </span>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        {/* Trust pill */}
        <div className="flex justify-center">
          <div
            data-testid="hero-trust-pill"
            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-brand-gold/40 rounded-full px-4 py-1.5 text-xs sm:text-sm text-brand-gold-light font-semibold"
          >
            <Sparkles className="w-3.5 h-3.5 text-brand-gold animate-pulse-soft" />
            <span className={isHi ? 'font-hindi' : 'font-outfit'}>
              {customerCount.toLocaleString('en-IN')}+ {isHi ? 'लोग अभी देख रहे हैं' : 'people viewing now'}
            </span>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center mt-10">
          {/* LEFT: interactive sample flipbook */}
          <div className="relative order-2 lg:order-1 opacity-0 animate-slide-in-left">
            <HeroFlipbook />
          </div>

          {/* RIGHT: copy + CTA */}
          <div className="order-1 lg:order-2 text-center lg:text-left opacity-0 animate-slide-in-right">
            <p className={`text-brand-gold uppercase tracking-[0.3em] text-xs sm:text-sm font-semibold mb-3 flex items-center justify-center lg:justify-start gap-2 ${isHi ? 'font-hindi' : ''}`}>
              <span className="inline-block w-8 h-px bg-brand-gold/60" />
              {isHi ? 'एस्ट्रो अविनाश द्वारा' : 'By Astro Avinash'}
              <span className="inline-block w-8 h-px bg-brand-gold/60" />
            </p>
            <h1
              className={`font-playfair font-bold text-4xl sm:text-5xl lg:text-6xl text-brand-gold-light leading-[1.05] mb-5 ${isHi ? 'font-hindi-serif' : ''}`}
              style={{ textShadow: '0 2px 30px rgba(74,9,18,0.5)' }}
            >
              {isHi ? (
                <>
                  क्या आपकी{' '}
                  <span
                    className="italic"
                    style={{
                      background: 'linear-gradient(90deg, #D4AF37 0%, #F3E5AB 50%, #D4AF37 100%)',
                      backgroundSize: '200% auto',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      animation: 'gold-text-shimmer 4s ease-in-out infinite',
                    }}
                  >
                    किस्मत
                  </span>
                  <br /> आपका साथ नहीं दे रही?
                </>
              ) : (
                <>
                  Is Your{' '}
                  <span
                    className="italic"
                    style={{
                      background: 'linear-gradient(90deg, #D4AF37 0%, #F3E5AB 50%, #D4AF37 100%)',
                      backgroundSize: '200% auto',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      animation: 'gold-text-shimmer 4s ease-in-out infinite',
                    }}
                  >
                    Destiny
                  </span>
                  <br /> Working Against You?
                </>
              )}
            </h1>
            <GoldDivider className="!my-3 lg:!mx-0 lg:justify-start" />
            <p
              className={`text-white/85 text-base md:text-lg leading-relaxed mb-7 max-w-xl mx-auto lg:mx-0 ${isHi ? 'font-hindi' : 'font-outfit'}`}
            >
              {isHi
                ? 'अपनी जन्म कुंडली से जानें — प्रेम, करियर, धन और स्वास्थ्य के सटीक रहस्य। 60+ पेज की विस्तृत वैदिक रिपोर्ट, हमारे अनुभवी ज्योतिषाचार्य द्वारा manually तैयार की जाती है।'
                : "Discover the hidden secrets of your birth chart — love, career, wealth & health. 60+ page detailed Vedic report, manually crafted by our experienced astrologer."}
            </p>

            {/* Price */}
            <div className="flex items-center gap-3 justify-center lg:justify-start mb-6 flex-wrap">
              <span className="text-white/50 line-through font-outfit text-xl">₹999</span>
              <div className="flex items-baseline gap-1">
                <span
                  className="font-outfit font-extrabold text-5xl md:text-6xl text-brand-gold-light"
                >
                  ₹99
                </span>
                <span className={`text-white/65 text-sm ${isHi ? 'font-hindi' : 'font-outfit'}`}>
                  / {isHi ? 'एक बार' : 'one time'}
                </span>
              </div>
              <span className="bg-brand-gold text-brand-maroon-dark text-xs font-black px-3 py-1 rounded-full uppercase tracking-wider">
                90% OFF
              </span>
            </div>

            <PremiumButton
              data-testid="get-started-btn"
              size="lg"
              onClick={onOpenForm}
              variant="gold"
              className="group !text-brand-maroon-dark"
            >
              <Sparkles className="w-4 h-4 group-hover:rotate-12 transition-transform" />
              <span className={`font-bold ${isHi ? 'font-hindi' : 'font-outfit'}`}>
                {isHi ? 'अभी अपनी कुंडली प्राप्त करें' : 'Get Your Personal Kundli Now'}
              </span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </PremiumButton>

            {/* Trust strip */}
            <div className="mt-8 grid grid-cols-3 gap-3 text-center lg:text-left max-w-md mx-auto lg:mx-0">
              <div className="bg-white/5 backdrop-blur-sm border border-brand-gold/20 rounded-xl p-3 hover:border-brand-gold/50 hover:-translate-y-0.5 transition-all">
                <div className="flex items-center justify-center lg:justify-start gap-1 text-brand-gold mb-1">
                  {[1,2,3,4,5].map(i => <Star key={i} className="w-3.5 h-3.5 fill-current" />)}
                </div>
                <p className={`text-xs text-white/75 ${isHi ? 'font-hindi' : 'font-outfit'}`}>
                  4.9/5 • 10K+ {isHi ? 'समीक्षाएं' : 'reviews'}
                </p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm border border-brand-gold/20 rounded-xl p-3 hover:border-brand-gold/50 hover:-translate-y-0.5 transition-all">
                <div className="flex items-center justify-center lg:justify-start gap-1 text-brand-gold mb-1">
                  <ShieldCheck className="w-4 h-4" />
                  <span className="font-bold text-sm text-brand-gold-light">100%</span>
                </div>
                <p className={`text-xs text-white/75 ${isHi ? 'font-hindi' : 'font-outfit'}`}>
                  {isHi ? 'सुरक्षित भुगतान' : 'Secure Payment'}
                </p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm border border-brand-gold/20 rounded-xl p-3 hover:border-brand-gold/50 hover:-translate-y-0.5 transition-all">
                <div className="flex items-center justify-center lg:justify-start gap-1 text-brand-gold mb-1">
                  <FlowerMotif className="w-4 h-4" />
                  <span className="font-bold text-sm text-brand-gold-light">15+</span>
                </div>
                <p className={`text-xs text-white/75 ${isHi ? 'font-hindi' : 'font-outfit'}`}>
                  {isHi ? 'वर्षों का अनुभव' : 'Years experience'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
